import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import {
    categoryAdminService,
    submissionsByDeskService,
    Category,
    DeskItem,
    reviewerEvaluationReportService,
    ReviewerEvaluationProcessItem,
    ReviewerEvaluationAssignmentSummary,
    ReviewerEvaluationFinalDecisionDetail,
    CategoryMemberDropdownItem,
    primaFacieService,
    manuscriptReviewByDeskService,
    SubmissionStatus,
    isAuthorMoveDecision,
    statusForAuthorMoveDecision,
} from '../services/adminAuth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/hooks/auth';
import { Eye, CheckCircle, Clock, XCircle, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';

// ── Constants ─────────────────────────────────────────────────────────────────

function ReviewerEvaluationReports() {
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const navigate = useNavigate();

    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isDESS = user?.roleId === 9 || user?.role === 'dess';
    const isTagChairman = user?.roleId === 5;
    const isDESSOrTagChairman = isDESS || isTagChairman;

    // ── Category resolution ───────────────────────────────────────────────────
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    // ── Table state ───────────────────────────────────────────────────────────
    const [submissions, setSubmissions] = useState<ReviewerEvaluationProcessItem[]>([]);
    const [loading, setLoading] = useState(true);

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [search, setSearch] = useState('');
    const [completedFilter, setCompletedFilter] = useState<boolean>(isCategoryAdmin ? false : true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // ── Final Decision Modal state ────────────────────────────────────────────
    const [isFinalDecisionOpen, setIsFinalDecisionOpen] = useState(false);
    const [finalDecisionTarget, setFinalDecisionTarget] = useState<ReviewerEvaluationProcessItem | null>(null);

    // Form fields
    const [fdDecision, setFdDecision] = useState<'' | '1' | '2' | '3' | '4' | '5' | '6'>('');
    const [fdAllowResubmission, setFdAllowResubmission] = useState<'' | 'true' | 'false'>('');
    const [fdComment, setFdComment] = useState('');
    const [fdToWorkflowStage, setFdToWorkflowStage] = useState<number | ''>('');

    // ── Add Panel Member modal ─────────────────────────────────────────────────
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [addPanelTarget, setAddPanelTarget] = useState<ReviewerEvaluationProcessItem | null>(null);
    const [addPanelMembers, setAddPanelMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingAddMembers, setFetchingAddMembers] = useState(false);
    const [selectedAddMemberIds, setSelectedAddMemberIds] = useState<(string | number)[]>([]);
    const [isAddingPanel, setIsAddingPanel] = useState(false);

    // ── Delete Panel Member modal ──────────────────────────────────────────────
    const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
    const [deletePanelTarget, setDeletePanelTarget] = useState<ReviewerEvaluationProcessItem | null>(null);
    const [selectedDeleteAssignmentId, setSelectedDeleteAssignmentId] = useState('');
    const [isDeletingMember, setIsDeletingMember] = useState(false);

    // Desk list
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);

    // Submitting
    const [isSubmittingFinalDecision, setIsSubmittingFinalDecision] = useState(false);

    // ── View Final Decision Drawer state ──────────────────────────────────────
    const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
    const [viewDecisionData, setViewDecisionData] = useState<ReviewerEvaluationFinalDecisionDetail | null>(null);
    const [loadingDecision, setLoadingDecision] = useState(false);

    // ── Change Submission Status (DESS only) ──────────────────────────────────
    const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
    const [statusTarget, setStatusTarget] = useState<ReviewerEvaluationProcessItem | null>(null);
    const [newStatus, setNewStatus] = useState<number | ''>('');
    const [statusComment, setStatusComment] = useState('');
    
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isApproveMode, setIsApproveMode] = useState(false);

    const [isSubmissionDetailsOpen, setIsSubmissionDetailsOpen] = useState(false);
    const [detailsSubmissionId, setDetailsSubmissionId] = useState<string | null>(null);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data) {
                    setCategories(res.data);
                    if (isCategoryAdmin) {
                        if (res.data.length > 0) {
                            const id = res.data[0].id;
                            setCategoryAdminCategoryId(id);
                            setSelectedCategoryId(id);
                        } else {
                            setCategoryAdminCategoryId(0);
                            setLoading(false);
                        }
                    } else {
                        setCategoryAdminCategoryId(0);
                    }
                } else {
                    if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
                }
            } catch {
                if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, []);

    // ── Fetch on filter / page change ─────────────────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => { fetchSubmissions(); }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, completedFilter, selectedCategoryId, categoryAdminCategoryId]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await reviewerEvaluationReportService.getReviewEvaluationsProcess({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                completed: isCategoryAdmin ? completedFilter : true,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : selectedCategoryId,
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch evaluations' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching evaluations' });
            } finally {
            setLoading(false);
        }
    };

    // ── Open final decision modal ─────────────────────────────────────────────
    const openFinalDecisionModal = async (row: ReviewerEvaluationProcessItem) => {
        const assignments = row.assignments ?? [];
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — at least 1 required):
        // if (assignments.length < 3) {
        //     showNotification('error', {
        //         title: 'Insufficient Panel Members',
        //         message: 'A minimum of 3 panel members is required to make a final decision. Please add more panel members before proceeding.',
        //     });
        //     return;
        // }
        if (assignments.length < 1) {
            showNotification('error', {
                title: 'No Panel Members Assigned',
                message: 'At least 1 panel member must be assigned to make a final decision. Please assign a panel member before proceeding.',
            });
            return;
        }

        const hasIncomplete = assignments.some(a => a.status?.toLowerCase() !== 'completed');
        if (hasIncomplete) {
            showNotification('error', {
                title: 'Cannot Make Final Decision',
                message: 'All panel evaluations must be completed before making a final decision. Please wait for pending evaluations to be submitted.',
            });
            return;
        }
        setFinalDecisionTarget(row);
        setFdDecision('');
        setFdAllowResubmission('');
        setFdComment('');
        setFdToWorkflowStage('');
        setIsFinalDecisionOpen(true);

        setDeskListLoading(true);
        try {
            const deskRes = await submissionsByDeskService.getDeskList();
            setDeskList(deskRes.isSuccess && deskRes.data ? deskRes.data : []);
        } catch {
            setDeskList([]);
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeFinalDecisionModal = () => {
        if (isSubmittingFinalDecision) return;
        setIsFinalDecisionOpen(false);
        setFinalDecisionTarget(null);
        setFdDecision('');
        setFdAllowResubmission('');
        setFdComment('');
        setFdToWorkflowStage('');
        setDeskList([]);
    };

    // ── Open view final decision drawer ──────────────────────────────────────
    const openViewDecisionDrawer = async (row: ReviewerEvaluationProcessItem) => {
        setIsViewDrawerOpen(true);
        setViewDecisionData(null);
        setLoadingDecision(true);
        try {
            const res = await reviewerEvaluationReportService.getFinalDecisionByManuscriptReviewId(row.manuscriptReviewId);
            if (res?.isSuccess === true && res.data) {
                setViewDecisionData(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to load final decision details' });
                setIsViewDrawerOpen(false);
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
            setIsViewDrawerOpen(false);
        } finally {
            setLoadingDecision(false);
        }
    };

    const handleSubmitFinalDecision = async () => {
        if (!finalDecisionTarget) return;
        if (!fdDecision) {
            showNotification('error', { title: 'Required', message: 'Please select a recommendation.' });
            return;
        }
        if (fdToWorkflowStage === '') {
            showNotification('error', { title: 'Required', message: 'Please select the next desk to move to.' });
            return;
        }
        if ((fdDecision === '2' || fdDecision === '4') && !fdAllowResubmission) { 
            showNotification('error', { title: 'Required', message: 'Please specify if resubmission is allowed.' });
            return;
        }

        setIsSubmittingFinalDecision(true);
        await new Promise(r => setTimeout(r, 0));

        try {
            const res = await reviewerEvaluationReportService.submitFinalDecision({
                manuscriptReviewId: finalDecisionTarget.manuscriptReviewId,
                decision: parseInt(fdDecision, 10),
                comment: fdComment,
                toWorkflowStage: fdToWorkflowStage as number,
                allowResubmission: (fdDecision === '2' || fdDecision === '4') ? fdAllowResubmission === 'true' : false,
            });

            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Decision Submitted', message: 'The final Reviewer Evaluation decision has been submitted successfully.' });
                closeFinalDecisionModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit final decision.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingFinalDecision(false);
        }
    };

    // ── Filter handlers ───────────────────────────────────────────────────────
    const handleSearch = (query: string) => { setSearch(query); setCurrentPage(1); };
    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };
    const handleClearFilters = () => {
        setSearch('');
        setCompletedFilter(isCategoryAdmin ? false : true);
        if (!isCategoryAdmin) setSelectedCategoryId(0);
        setCurrentPage(1);
    };

    const handleViewEvaluation = (assignmentId: string) => {
        navigate(`/admin/reviewer-evaluation/${assignmentId}`);
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getStatusChip = (status: string) => {
        const lower = status?.toLowerCase();
        if (lower === 'completed' || lower === 'submitted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700">
                    <CheckCircle className="w-3 h-3" />{status}
                </span>
            );
        }
        if (lower === 'pending' || lower === 'notstarted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                    <Clock className="w-3 h-3" />Pending
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">
                <XCircle className="w-3 h-3" />{status}
            </span>
        );
    };

    const formatFinalDecision = (decision: string | null) => {
        switch (decision) {
            case 'AcceptableForPublication':    return 'Acceptable for Publication';
            case 'NotAcceptableForPublication': return 'Not Acceptable for Publication';
            case 'AcceptableForFunding':        return 'Acceptable for Funding';
            case 'NotAcceptableForFunding':     return 'Not Acceptable for Funding';
            case 'ReturnToReviewer':            return 'Return to Reviewer for Re-Assessment';
            case 'ReturnToAuthorForRevisedCopy': return 'Return to Author for Revised Copy';
            default:                            return decision ?? '—';
        }
    };

    const isNotAcceptable = (decision: string | null) => decision === 'NotAcceptableForPublication' || decision === 'NotAcceptableForFunding';

    const openAddPanelModal = async (row: ReviewerEvaluationProcessItem) => {
        setAddPanelTarget(row);
        setSelectedAddMemberIds([]);
        setIsAddPanelOpen(true);
        setFetchingAddMembers(true);
        try {
            const response = await primaFacieService.getCategoryMemberDropdown(row.categoryId);
            if (response.isSuccess && response.data) {
                // Exclude existing panel members
                const existingIds = new Set(row.assignments.map(a => a.memberId));
                setAddPanelMembers(response.data.filter(m => !existingIds.has(m.id)));
            } else {
                setAddPanelMembers([]);
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load members' });
            }
        } catch {
            setAddPanelMembers([]);
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setFetchingAddMembers(false);
        }
    };

    const closeAddPanelModal = () => {
        setIsAddPanelOpen(false);
        setAddPanelTarget(null);
        setSelectedAddMemberIds([]);
        setAddPanelMembers([]);
    };

    const handleAddPanelMembers = async () => {
        if (!addPanelTarget || selectedAddMemberIds.length === 0) return;
        setIsAddingPanel(true);
        try {
            const assignedMembers = selectedAddMemberIds.map(id => {
                const member = addPanelMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });
            const response = await manuscriptReviewByDeskService.assignReviewerEvaluationPanel({
                manuscriptReviewId: addPanelTarget.manuscriptReviewId,
                assignedMembers,
            });
            if (response.isSuccess) {
                const results = response.data ?? [];
                const failed = results.filter(r => !r.successful);
                if (failed.length > 0) {
                    showNotification('error', {
                        title: 'Partial Assignment',
                        message: `${failed.length} member(s) could not be assigned: ${failed.map(f => f.reason).join(', ')}`,
                    });
                } else {
                    showNotification('success', { title: 'Success', message: 'Panel member(s) added successfully' });
                }
                closeAddPanelModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to add members' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsAddingPanel(false);
        }
    };

    const openDeletePanelModal = (row: ReviewerEvaluationProcessItem) => {
        setDeletePanelTarget(row);
        setSelectedDeleteAssignmentId('');
        setIsDeletePanelOpen(true);
    };

    const closeDeletePanelModal = () => {
        if (isDeletingMember) return;
        setIsDeletePanelOpen(false);
        setDeletePanelTarget(null);
        setSelectedDeleteAssignmentId('');
    };

    const handleDeletePanelMember = async () => {
        if (!selectedDeleteAssignmentId) {
            showNotification('error', { title: 'Required', message: 'Please select a member to remove.' });
            return;
        }
        setIsDeletingMember(true);
        try {
            const response = await reviewerEvaluationReportService.deletePanelMember(selectedDeleteAssignmentId);
            if (response?.isSuccess) {
                showNotification('success', { title: 'Removed', message: 'Panel member removed successfully.' });
                closeDeletePanelModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response?.message || 'Failed to remove member.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsDeletingMember(false);
        }
    };

     // ── Change Status handlers (DESS only) ────────────────────────────────────
    const openChangeStatusModal = (row: ReviewerEvaluationProcessItem) => {
        setStatusTarget(row);
        setNewStatus('');
        setStatusComment('');
        // If final decision accepted for publication, this should be an "approve" flow
        const approve = row.finalDecision === 'AcceptableForPublication';
        setIsApproveMode(approve);
        if (approve) setNewStatus(SubmissionStatus.Approved);
        setIsChangeStatusModalOpen(true);
    };

    const closeChangeStatusModal = () => {
        setIsChangeStatusModalOpen(false);
        setStatusTarget(null);
        setNewStatus('');
        setStatusComment('');
        setIsApproveMode(false);
    };

    const handleChangeStatus = async () => {
        if (!statusTarget || newStatus === '') return;
        setIsUpdatingStatus(true);
        try {
            const payload = {
                manuscriptReviewId: statusTarget.manuscriptReviewId,
                comment: statusComment.trim() || '',
                submissionStatus: newStatus,
            };
            const response = await manuscriptReviewByDeskService.moveManuscriptReviewToAuthor(payload as any);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: response.message || 'Submission moved to author successfully.' });
                closeChangeStatusModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to move submission to author.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while updating status.' });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleViewReviewForm = (submission: ReviewerEvaluationProcessItem) => {
        navigate(`/admin/review-form/${submission.reviewAssignmentId}`);
    };

    const handleViewSubmissionDetails = (submissionId: string) => {
        setDetailsSubmissionId(submissionId);
        setIsSubmissionDetailsOpen(true);
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns: TableColumn<ReviewerEvaluationProcessItem>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => <span className="text-gray-500 text-sm font-mono">{value}</span>,
        },
        {
            key: 'title',
            label: 'Title / Reviewer',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5 leading-snug">{row.title}</div>
                    <div className="text-xs text-gray-500">Reviewer: {row.reviewerFullName}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Author: {row.authorFullName}</div>
                </div>
            ),
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {value || 'N/A'}
                </span>
            ),
        },
        {
            key: 'completedEvaluation',
            label: 'Final Decision Made?',
            render: (value, row) => value ? (
                <div className="space-y-2 flex flex-col">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" />Yes
                    </span>
                    {isNotAcceptable(row.finalDecision) ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700">
                            {formatFinalDecision(row.finalDecision)}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700">
                            {formatFinalDecision(row.finalDecision)}
                        </span>
                    )}
                    {isNotAcceptable(row.finalDecision) && (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                            row.allowResubmission
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-gray-100 text-gray-500'
                        }`}>
                            {row.allowResubmission
                                ? <>Resubmission allowed</>
                                : <>No resubmission</>
                            }
                        </span>
                    )}
                </div>
            ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700">
                    <Clock className="w-3.5 h-3.5" />No
                </span>
            ),
        },
        {
            key: 'assignments',
            label: 'Assigned Evaluation Panel',
            render: (value, row) => {
                const assignments: ReviewerEvaluationAssignmentSummary[] = row.assignments ?? [];
                if (assignments.length === 0) {
                    return <span className="text-xs text-gray-400 italic">No panel assigned</span>;
                }
                return (
                    <div className="space-y-1.5">
                        {assignments.map((a, idx) => (
                            <div key={a.reviewerEvaluationAssignmentId} className="flex items-center gap-2 min-h-[28px]">
                                <div className="w-5 h-5 border rounded-full bg-gray-100 flex items-center justify-center text-gray-900 font-bold text-[10px] flex-shrink-0">
                                    {idx + 1}
                                </div>
                                <span className="text-xs text-gray-700 font-medium flex-1 min-w-0 truncate">
                                    {a.memberName}
                                </span>
                                {getStatusChip(a.status)}
                                <div className="w-6 flex-shrink-0 flex justify-end">
                                    {a.status?.toLowerCase() === 'completed' && a.reviewerEvaluationId && (
                                        <button
                                            onClick={() => handleViewEvaluation(a.reviewerEvaluationAssignmentId)}
                                            title="View Reviewer Evaluation"
                                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-[#088A61] transition-colors"
                                        >
                                            <Eye className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    // ── Actions ───────────────────────────────────────────────────────────────
    const actions: TableAction<ReviewerEvaluationProcessItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Submission Details',
                    onClick: (row: ReviewerEvaluationProcessItem) => handleViewSubmissionDetails(row.submissionId),
                    className: 'text-blue-600',
                },
                {
                    label: 'View Review Details',
                    onClick: handleViewReviewForm,
                    className: 'text-blue-600',
                },
                {
                    label: 'Make Final Decision',
                    onClick: (row) => openFinalDecisionModal(row),
                    className: (row) =>
                        !isCategoryAdmin || row.completedEvaluation ? 'hidden' : 'text-blue-600',
                },
                {
                    label: 'View Final Decision',
                    onClick: (row) => openViewDecisionDrawer(row),
                    className: (row) => (!row.completedEvaluation ? 'hidden' : 'text-blue-600'),
                },
                {
                    label: 'Add Panel Member',
                    onClick: (row) => openAddPanelModal(row),
                    className: (row) =>
                        row.completedEvaluation ? 'hidden' : 'text-green-600',
                },
                {
                    label: 'Delete Panel Member',
                    onClick: (row) => openDeletePanelModal(row),
                    className: (row) => {
                        const hasRemovable = row.assignments.some(
                            a => a.status?.toLowerCase() === 'not started'
                        );
                        return row.completedEvaluation || !hasRemovable ? 'hidden' : 'text-red-600';
                    },
                },
                // Move submission to Author — DESS or TAG Chairman, gated by final decision
                {
                    label: (row: ReviewerEvaluationProcessItem) => (
                        row.finalDecision === 'AcceptableForPublication' ? 'Approve Manuscript' : 'Move submission to Author'
                    ),
                    onClick: (row: ReviewerEvaluationProcessItem) => openChangeStatusModal(row),
                    className: (row: ReviewerEvaluationProcessItem) =>
                        isDESSOrTagChairman && isAuthorMoveDecision(row.finalDecision)
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Category dropdown for filter ──────────────────────────────────────────
    const renderCategoryDropdown = () => {
        if (isCategoryAdmin) {
            if (categoryAdminCategoryId === 0 || categories.length === 0) {
                return (
                    <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400 cursor-not-allowed">
                        <option>No Thematic Area</option>
                    </select>
                );
            }
            return (
                <select disabled value={categories[0].id} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    <option value={categories[0].id}>{categories[0].name}</option>
                </select>
            );
        }
        return (
            <select
                value={selectedCategoryId}
                onChange={(e) => { setSelectedCategoryId(parseInt(e.target.value)); setCurrentPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value={0}>All Thematic Areas</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
        );
    };

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    {renderCategoryDropdown()}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Status</label>
                    {isCategoryAdmin ? (
                        <select
                            value={completedFilter ? 'true' : 'false'}
                            onChange={(e) => { setCompletedFilter(e.target.value === 'true'); setCurrentPage(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="false">In Progress</option>
                            <option value="true">Completed</option>
                        </select>
                    ) : (
                        <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                            <option value="true">Completed</option>
                        </select>
                    )}
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear filters
            </button>
        </div>
    );

    const emptyMessage = isCategoryAdmin && categoryAdminCategoryId === 0
        ? 'No Thematic Area assigned to this admin yet'
        : 'No evaluations found under the Reviewer Evaluation process.';

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Reviewer Evaluation Reports</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Track manuscript reviews currently under the Reviewer Evaluation process.
                </p>
            </div>

            <ReusableTable
                title="Reviewer Evaluation Process"
                subTitle="Manuscript reviews and their evaluation panel assignments"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, reviewer, or ID..."
                onSearch={handleSearch}
                emptyMessage={emptyMessage}
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── Make Final Decision Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isFinalDecisionOpen}
                onClose={closeFinalDecisionModal}
                title="Make Final Reviewer Evaluation Decision"
                description="This decision is final and cannot be undone. Review carefully before submitting."
                maxWidth="lg"
            >
                <div className="space-y-3 mt-1">

                    {finalDecisionTarget && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Manuscript Review</p>
                            <p className="text-sm font-semibold text-gray-900 line-clamp-1">{finalDecisionTarget.title}</p>
                            <p className="text-xs text-gray-400">{finalDecisionTarget.submissionNumber} · {finalDecisionTarget.categoryName}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Reviewer: {finalDecisionTarget.reviewerFullName}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">
                            This is <strong>irreversible</strong>. Ensure all panel evaluations have been considered.
                        </p>
                    </div>

                    {/* Row 1: Recommendation + Move to Desk */}
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Recommendation <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={fdDecision}
                                onChange={e => {
                                    setFdDecision(e.target.value as '' | '1' | '2' | '3' | '4' | '5' | '6');
                                    setFdAllowResubmission('');
                                }}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select…</option>
                                {finalDecisionTarget?.applicationType === 1 ? (
                                    <>
                                        {/* <option value="1">Acceptable for Publication</option> */}
                                        {/* <option value="2">Not Acceptable for Publication</option> */}
                                        <option value="5">Return to Reviewer for Re-Assessment</option>
                                        <option value="6">Process further to Author for Revised Copy</option>
                                    </>
                                ) : finalDecisionTarget?.applicationType === 2 ? (
                                    <>
                                        <option value="3">Acceptable for Funding</option>
                                        <option value="4">Not Acceptable for Funding</option>
                                        <option value="5">Return to Reviewer for Re-Assessment</option>
                                        <option value="6">Process further to Author for Revised Copy</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="1">Acceptable for Publication</option>
                                        <option value="2">Not Acceptable for Publication</option>
                                        <option value="3">Acceptable for Funding</option>
                                        <option value="4">Not Acceptable for Funding</option>
                                        <option value="5">Return to Reviewer for Re-Assessment</option>
                                        <option value="6">Process further to Author for Revised Copy</option>
                                    </>
                                )}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Move to Next Desk <span className="text-red-500">*</span>
                            </label>
                            {deskListLoading ? (
                                <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                    <Spinner />Loading…
                                </div>
                            ) : (
                                <select
                                    value={fdToWorkflowStage}
                                    onChange={e => setFdToWorkflowStage(e.target.value === '' ? '' : Number(e.target.value))}
                                    className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                                >
                                    <option value="">Select desk…</option>
                                    {deskList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>

                    {/* Allow resubmission — only when Not Recommended */}
                    {(fdDecision === '2' || fdDecision === '4') && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Allow Resubmission? <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={fdAllowResubmission}
                                onChange={e => setFdAllowResubmission(e.target.value as '' | 'true' | 'false')}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select…</option>
                                <option value="true">Yes — allow resubmission</option>
                                <option value="false">No — do not allow resubmission</option>
                            </select>
                        </div>
                    )}

                    {/* Comment */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={fdComment}
                            onChange={e => setFdComment(e.target.value)}
                            placeholder="Add any notes about this decision…"
                            rows={2}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeFinalDecisionModal}
                            disabled={isSubmittingFinalDecision}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitFinalDecision}
                            disabled={isSubmittingFinalDecision}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmittingFinalDecision ? <><Spinner />Submitting…</> : 'Submit Final Decision'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── View Final Decision Drawer ────────────────────────────────── */}
            <Drawer
                isOpen={isViewDrawerOpen}
                onClose={() => { setIsViewDrawerOpen(false); setViewDecisionData(null); }}
                title="Final Reviewer Evaluation Decision"
                width="max-w-2xl"
            >
                {loadingDecision ? (
                    <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
                        <Spinner />
                        <span className="text-sm">Loading decision details…</span>
                    </div>
                ) : 
                viewDecisionData ? (() => {
                    const d = viewDecisionData;

                    const formatDate = (s: string) => s
                        ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
                        : '—';

                    const decisionConfig: Record<number, { label: string; positive: boolean }> = {
                        1: { label: 'Acceptable for Publication',         positive: true  },
                        2: { label: 'Not Acceptable for Publication',     positive: false },
                        3: { label: 'Acceptable for Funding',             positive: true  },
                        4: { label: 'Not Acceptable for Funding',         positive: false },
                        5: { label: 'Return to Reviewer for Re-Assessment', positive: false },
                        6: { label: 'Return to Author for Revised Copy',    positive: true },
                    };

                    const config = decisionConfig[d.decision] ?? { label: '—', positive: true };
                    const isPositive = config.positive;
                    const isNotAcceptableDecision = !isPositive;

                    return (
                        <div className="space-y-0 divide-y divide-gray-100">

                            {/* ── Decision badge ── */}
                            <div className="pb-5 space-y-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    {isPositive ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700">
                                            <CheckCircle className="w-3.5 h-3.5" />{config.label}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700">
                                            <XCircle className="w-3.5 h-3.5" />{config.label}
                                        </span>
                                    )}
                                    {/* {isNotAcceptableDecision && (
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                                            d.allowResubmission ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {d.allowResubmission
                                                ? <><Clock className="w-3.5 h-3.5" />Resubmission Allowed</>
                                                : <><XCircle className="w-3.5 h-3.5" />No Resubmission</>
                                            }
                                        </span>
                                    )} */}
                                </div>
                            </div>

                            {/* ── Decision details ── */}
                            <div className="py-5 space-y-4">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Final Decision Details</h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className={`rounded-lg p-3 border ${isPositive ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Decision</p>
                                        <p className={`text-sm font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                                            {config.label}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Decision Date</p>
                                        <p className="text-sm font-medium text-gray-800">{formatDate(d.dateCreated)}</p>
                                    </div>
                                    {/* {isNotAcceptableDecision && (
                                        <div className={`rounded-lg p-3 border ${d.allowResubmission ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                                            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Resubmission</p>
                                            <p className={`text-sm font-bold ${d.allowResubmission ? 'text-amber-700' : 'text-gray-600'}`}>
                                                {d.allowResubmission ? 'Allowed' : 'Not Allowed'}
                                            </p>
                                        </div>
                                    )} */}
                                </div>

                                {d.comment && (
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Comment</p>
                                        <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                            {d.comment}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* ── Decided by ── */}
                            <div className="py-5">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Decision Made By</h3>
                                {d.decidedBy?.person ? (
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                            {d.decidedBy.person.firstName?.[0]}{d.decidedBy.person.lastName?.[0]}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-900 text-sm">{d.decidedBy.person.fullName}</p>
                                            <p className="text-xs text-gray-500">{d.decidedBy.person.email}</p>
                                            {d.decidedBy.person.institution?.name && (
                                                <p className="text-xs text-gray-400 mt-0.5">{d.decidedBy.person.institution.name}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Decider information unavailable</p>
                                )}
                            </div>

                        </div>
                    );
                })() : null}
            </Drawer>

            {/* ── Add Panel Member Modal ────────────────────────────────────────────── */}
            <Modal
                isOpen={isAddPanelOpen}
                onClose={closeAddPanelModal}
                title="Add Panel Member"
                description="Select one or more thematic area members to add to the Reviewer Evaluation panel."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {addPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Manuscript</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{addPanelTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{addPanelTarget.categoryName}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Members to Add <span className="text-red-500">*</span>
                        </label>
                        {fetchingAddMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading members…
                            </div>
                        ) : addPanelMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No additional members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={addPanelMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedAddMemberIds}
                                onChange={setSelectedAddMemberIds}
                                placeholder="Select members to add..."
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAddPanelModal} disabled={isAddingPanel}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAddPanelMembers} disabled={isAddingPanel || selectedAddMemberIds.length === 0}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAddingPanel ? <><Spinner />Adding…</> : 'Add Member(s)'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Delete Panel Member Modal ─────────────────────────────────────────── */}
            <Modal
                isOpen={isDeletePanelOpen}
                onClose={closeDeletePanelModal}
                title="Remove Panel Member"
                description="Only members who have not yet started their assignment can be removed."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {deletePanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Manuscript</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{deletePanelTarget.title}</p>
                        </div>
                    )}
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium">This will permanently remove the member from this evaluation panel.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select Member to Remove <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedDeleteAssignmentId}
                            onChange={e => setSelectedDeleteAssignmentId(e.target.value)}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="">Select member…</option>
                            {deletePanelTarget?.assignments
                                .filter(a => a.status?.toLowerCase() === 'not started')
                                .map(a => (
                                    <option key={a.reviewerEvaluationAssignmentId} value={a.reviewerEvaluationAssignmentId}>
                                        {a.memberName}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeDeletePanelModal} disabled={isDeletingMember}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleDeletePanelMember} disabled={isDeletingMember || !selectedDeleteAssignmentId}
                            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isDeletingMember ? <><Spinner />Removing…</> : 'Remove Member'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Change Submission Status Modal (DESS only) ── */}
            <Modal
                isOpen={isChangeStatusModalOpen}
                onClose={closeChangeStatusModal}
                title={isApproveMode ? 'Approve Manuscript' : 'Communicate Submission Status'}
                description={isApproveMode ? 'Approve the manuscript and set its submission status to Approved.' : 'Move the reviewed manuscript back to the author with an optional comment.'}
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {statusTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {statusTarget.title ?? '—'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">
                                {statusTarget.submissionNumber ?? '—'}
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Submission Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={newStatus}
                            onChange={(e) => {
                                    const val = e.target.value === '' ? '' : Number(e.target.value);
                                    setNewStatus(val);
                                }}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        >
                            <option value="">Select Status...</option>
                            {(() => {
                                const dec = statusTarget?.finalDecision;
                                if (!isAuthorMoveDecision(dec)) return null;
                                const opt = statusForAuthorMoveDecision(dec);
                                return <option value={opt.value}>{opt.label}</option>;
                            })()}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={statusComment}
                            onChange={(e) => setStatusComment(e.target.value)}
                            placeholder="Add a comment regarding this status change..."
                            rows={4}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />

                        {/* Removed 'Send reviewed manuscript to author' toggle — system handles file sending */}
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeChangeStatusModal}
                            disabled={isUpdatingStatus}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleChangeStatus}
                            disabled={newStatus === '' || isUpdatingStatus}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUpdatingStatus ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    {isApproveMode ? 'Approving...' : 'Moving...'}
                                </>
                            ) : (
                                <>
                                    {isApproveMode ? <CheckCircle className="w-4 h-4" /> : <ArrowRightLeft className="w-4 h-4" />}
                                    {isApproveMode ? 'Approve' : 'Move'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <Drawer
                isOpen={isSubmissionDetailsOpen}
                onClose={() => { setIsSubmissionDetailsOpen(false); setDetailsSubmissionId(null); }}
                title="Submission Details"
                width="max-w-3xl"
            >
                {detailsSubmissionId && (
                    <AdminSubmissionDetails submissionId={detailsSubmissionId} />
                )}
            </Drawer>
        </div>
    );
}

export default ReviewerEvaluationReports;